import React, { useState, useRef, useEffect } from "react";
import { Modal, Tabs, Input, Select, Switch, Button, message, Card } from "antd";
import { BsTrash, BsPlus } from "react-icons/bs";
import { FiImage } from "react-icons/fi";
import { MdDragIndicator } from "react-icons/md";
import { updateSubcategory } from "../../../api/AdminRequest";

const { TabPane } = Tabs;

function EditSubCategoryModal({ visible, onClose, subcategory, onSuccess }) {
    // General tab state
    const [name, setName] = useState("");
    const [value, setValue] = useState("");
    const [status, setStatus] = useState("active");
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);

    // Forms tab state
    const [formFields, setFormFields] = useState([]);
    const [submitLoading, setSubmitLoading] = useState(false);

    // Gallery Initial state
    const [gallerySections, setGallerySections] = useState([]);

    // Field type options
    const fieldTypes = [
        { value: "text", label: "Text Input" },
        { value: "textarea", label: "Text Area" },
        { value: "select", label: "Dropdown Select" },
        { value: "date", label: "Date Picker" },
    ];

    const pickingTypes = [
        { value: "date", label: "Full Date" },
        { value: "year", label: "Year Only" },
        { value: "month", label: "Month & Year" },
    ];

    // Initialize form with subcategory data when modal opens
    useEffect(() => {
        if (subcategory && visible) {
            setName(subcategory.name || "");
            setValue(subcategory.value || "");
            setStatus(subcategory.status || "active");
            setImagePreview(subcategory.imageUrl || null);
            setImageFile(null);

            // Initialize form fields with IDs
            const existingFields = (subcategory.form || []).map((field, index) => ({
                ...field,
                id: field.id || Date.now() + index,
            }));
            setFormFields(existingFields);

            // Convert galleryInitial object to array format
            const galleryInitial = subcategory.galleryInitial || {};
            const sections = Object.keys(galleryInitial).map((key, index) => ({
                id: Date.now() + index,
                name: key,
            }));
            setGallerySections(sections);
        }
    }, [subcategory, visible]);

    // Image handlers
    const handleImageClick = () => {
        fileInputRef.current?.click();
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                message.error("Please upload an image file");
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                message.error("Image size should be less than 5MB");
                return;
            }
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const removeImage = () => {
        setImageFile(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // Form field handlers
    const addFormField = () => {
        const newField = {
            id: Date.now(),
            label: "",
            name: "",
            type: "text",
            required: false,
            show: true,
            icon: null,
            values: [],
            pickingType: "date",
        };
        setFormFields([...formFields, newField]);
    };

    const updateFormField = (index, key, value) => {
        const updatedFields = [...formFields];
        updatedFields[index][key] = value;

        if (key === "label") {
            updatedFields[index].name = value
                .toLowerCase()
                .replace(/\s+/g, "")
                .replace(/[^a-zA-Z0-9]/g, "");
        }

        setFormFields(updatedFields);
    };

    const removeFormField = (index) => {
        const updatedFields = formFields.filter((_, i) => i !== index);
        setFormFields(updatedFields);
    };

    // Select values handlers
    const addSelectValue = (fieldIndex) => {
        const updatedFields = [...formFields];
        updatedFields[fieldIndex].values.push({ label: "", value: "" });
        setFormFields(updatedFields);
    };

    const updateSelectValue = (fieldIndex, valueIndex, key, val) => {
        const updatedFields = [...formFields];
        updatedFields[fieldIndex].values[valueIndex][key] = val;

        if (key === "label") {
            updatedFields[fieldIndex].values[valueIndex].value = val
                .toLowerCase()
                .replace(/\s+/g, "_");
        }

        setFormFields(updatedFields);
    };

    const removeSelectValue = (fieldIndex, valueIndex) => {
        const updatedFields = [...formFields];
        updatedFields[fieldIndex].values = updatedFields[fieldIndex].values.filter(
            (_, i) => i !== valueIndex
        );
        setFormFields(updatedFields);
    };

    // Gallery handlers
    const addGallerySection = () => {
        setGallerySections([...gallerySections, { id: Date.now(), name: "" }]);
    };

    const updateGallerySection = (index, value) => {
        const updated = [...gallerySections];
        updated[index].name = value;
        setGallerySections(updated);
    };

    const removeGallerySection = (index) => {
        setGallerySections(gallerySections.filter((_, i) => i !== index));
    };

    // Convert gallerySections to the required format
    const getGalleryInitialData = () => {
        const galleryInitial = {};
        gallerySections.forEach((section) => {
            if (section.name.trim()) {
                galleryInitial[section.name.trim()] = [""];
            }
        });
        return galleryInitial;
    };

    const handleSubmit = async () => {
        if (!name.trim()) {
            message.error("Please enter subcategory name");
            return;
        }
        if (!value.trim()) {
            message.error("Please enter subcategory value");
            return;
        }
        if (!imagePreview) {
            message.error("Please select a subcategory image");
            return;
        }
        if (formFields.length === 0) {
            message.error("Please add at least one form field");
            return;
        }
        if (gallerySections.length === 0) {
            message.error("Please add at least one gallery section");
            return;
        }

        try {
            setSubmitLoading(true);

            const formData = new FormData();
            formData.append("name", name.trim());
            formData.append("value", value.trim());
            formData.append("status", status);
            formData.append("form", JSON.stringify(formFields.map(({ id, ...rest }) => rest)));
            formData.append("galleryInitial", JSON.stringify(getGalleryInitialData()));

            if (imageFile) {
                formData.append("image", imageFile);
            }

            const { data } = await updateSubcategory(subcategory._id, formData);
            if (data?.success) {
                message.success("Subcategory updated successfully!");
                onSuccess?.();
                onClose();
            }
        } catch (error) {
            console.log(error);
            message.error(error?.response?.data?.message || "Failed to update subcategory");
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleClose = () => {
        setName("");
        setValue("");
        setStatus("active");
        setImageFile(null);
        setImagePreview(null);
        setFormFields([]);
        setGallerySections([]);
        onClose();
    };

    return (
        <Modal
            title="Edit Subcategory"
            open={visible}
            onCancel={handleClose}
            footer={null}
            width={800}
            destroyOnClose
        >
            <div className="pt-4">
                <Tabs defaultActiveKey="general" className="category-tabs">
                    {/* General Tab */}
                    <TabPane tab="General" key="general">
                        <div className="space-y-6 max-w-2xl">
                            {/* Image Upload */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Subcategory Image <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleImageChange}
                                    accept="image/*"
                                    className="hidden"
                                />
                                {imagePreview ? (
                                    <div className="relative inline-block">
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                                        />
                                        <button
                                            onClick={removeImage}
                                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                                        >
                                            <BsTrash className="text-sm" />
                                        </button>
                                    </div>
                                ) : (
                                    <div
                                        onClick={handleImageClick}
                                        className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#1955EB] hover:bg-gray-50 transition-colors"
                                    >
                                        <FiImage className="text-3xl text-gray-400 mb-2" />
                                        <span className="text-xs text-gray-500">Click to upload</span>
                                    </div>
                                )}
                            </div>

                            {/* Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Subcategory Name <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    placeholder="Enter subcategory name (e.g., Cars)"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    size="large"
                                />
                            </div>

                            {/* Value */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Subcategory Value <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    placeholder="Enter subcategory value (e.g., cars)"
                                    value={value}
                                    onChange={(e) => setValue(e.target.value)}
                                    size="large"
                                />
                            </div>

                            {/* Status */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Status
                                </label>
                                <Select
                                    value={status}
                                    onChange={(val) => setStatus(val)}
                                    className="w-full"
                                    size="large"
                                    options={[
                                        { value: "active", label: "Active" },
                                        { value: "inactive", label: "Inactive" },
                                    ]}
                                />
                            </div>
                        </div>
                    </TabPane>

                    {/* Forms Tab */}
                    <TabPane tab="Form Builder" key="forms">
                        <div className="space-y-4">
                            <div className="flex justify-between items-center mb-4">
                                <p className="text-gray-600 text-sm">
                                    Define custom fields for this subcategory
                                </p>
                                <Button
                                    type="primary"
                                    icon={<BsPlus className="text-lg" />}
                                    onClick={addFormField}
                                    className="bg-[#1955EB] hover:bg-[#1945E0]"
                                >
                                    Add Field
                                </Button>
                            </div>

                            {formFields.length === 0 ? (
                                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                                    <p className="text-gray-500">No form fields added yet</p>
                                    <p className="text-gray-400 text-sm mt-1">
                                        Click "Add Field" to create custom form fields
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-4 max-h-[400px] overflow-y-auto">
                                    {formFields.map((field, index) => (
                                        <Card
                                            key={field.id}
                                            className="border border-gray-200 shadow-sm"
                                            size="small"
                                        >
                                            <div className="space-y-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <MdDragIndicator className="text-gray-400 cursor-move" />
                                                        <span className="font-medium text-gray-700">
                                                            Field {index + 1}
                                                        </span>
                                                    </div>
                                                    <Button
                                                        danger
                                                        size="small"
                                                        icon={<BsTrash />}
                                                        onClick={() => removeFormField(index)}
                                                    />
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1">
                                                            Label
                                                        </label>
                                                        <Input
                                                            placeholder="Field label"
                                                            value={field.label}
                                                            onChange={(e) =>
                                                                updateFormField(index, "label", e.target.value)
                                                            }
                                                            size="small"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1">
                                                            Name (auto)
                                                        </label>
                                                        <Input
                                                            placeholder="field_name"
                                                            value={field.name}
                                                            onChange={(e) =>
                                                                updateFormField(index, "name", e.target.value)
                                                            }
                                                            size="small"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1">
                                                            Type
                                                        </label>
                                                        <Select
                                                            value={field.type}
                                                            onChange={(val) => updateFormField(index, "type", val)}
                                                            className="w-full"
                                                            size="small"
                                                            options={fieldTypes}
                                                        />
                                                    </div>

                                                    {field.type === "date" && (
                                                        <div>
                                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                                                Picking Type
                                                            </label>
                                                            <Select
                                                                value={field.pickingType}
                                                                onChange={(val) =>
                                                                    updateFormField(index, "pickingType", val)
                                                                }
                                                                className="w-full"
                                                                size="small"
                                                                options={pickingTypes}
                                                            />
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="flex flex-wrap gap-6 pt-2">
                                                    <div className="flex items-center gap-2">
                                                        <Switch
                                                            checked={field.required}
                                                            onChange={(val) =>
                                                                updateFormField(index, "required", val)
                                                            }
                                                            size="small"
                                                        />
                                                        <span className="text-sm text-gray-600">Required</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Switch
                                                            checked={field.show}
                                                            onChange={(val) => updateFormField(index, "show", val)}
                                                            size="small"
                                                        />
                                                        <span className="text-sm text-gray-600">Show</span>
                                                    </div>
                                                </div>

                                                {field.type === "select" && (
                                                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                                        <div className="flex justify-between items-center mb-3">
                                                            <label className="text-xs font-medium text-gray-500">
                                                                Dropdown Options
                                                            </label>
                                                            <Button
                                                                type="dashed"
                                                                size="small"
                                                                icon={<BsPlus />}
                                                                onClick={() => addSelectValue(index)}
                                                            >
                                                                Add Option
                                                            </Button>
                                                        </div>
                                                        <div className="space-y-2">
                                                            {field.values.map((val, valIndex) => (
                                                                <div
                                                                    key={valIndex}
                                                                    className="flex items-center gap-2"
                                                                >
                                                                    <Input
                                                                        placeholder="Label"
                                                                        value={val.label}
                                                                        onChange={(e) =>
                                                                            updateSelectValue(
                                                                                index,
                                                                                valIndex,
                                                                                "label",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        size="small"
                                                                        className="flex-1"
                                                                    />
                                                                    <Input
                                                                        placeholder="Value"
                                                                        value={val.value}
                                                                        onChange={(e) =>
                                                                            updateSelectValue(
                                                                                index,
                                                                                valIndex,
                                                                                "value",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        size="small"
                                                                        className="flex-1"
                                                                    />
                                                                    <Button
                                                                        danger
                                                                        size="small"
                                                                        icon={<BsTrash className="text-xs" />}
                                                                        onClick={() => removeSelectValue(index, valIndex)}
                                                                    />
                                                                </div>
                                                            ))}
                                                            {field.values.length === 0 && (
                                                                <p className="text-gray-400 text-xs text-center py-2">
                                                                    No options added
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </div>
                    </TabPane>

                    {/* Gallery Initial Tab */}
                    <TabPane tab="Gallery Sections" key="gallery">
                        <div className="space-y-4">
                            <div className="flex justify-between items-center mb-4">
                                <div>
                                    <p className="text-gray-600 text-sm">
                                        Define image gallery sections for this subcategory
                                    </p>
                                    <p className="text-gray-400 text-xs mt-1">
                                        These sections will be used for image uploads when creating bids
                                    </p>
                                </div>
                                <Button
                                    type="primary"
                                    icon={<BsPlus className="text-lg" />}
                                    onClick={addGallerySection}
                                    className="bg-[#1955EB] hover:bg-[#1945E0]"
                                >
                                    Add Section
                                </Button>
                            </div>

                            {gallerySections.length === 0 ? (
                                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                                    <FiImage className="text-4xl text-gray-400 mx-auto mb-3" />
                                    <p className="text-gray-500">No gallery sections added yet</p>
                                    <p className="text-gray-400 text-sm mt-1">
                                        Click "Add Section" to create gallery sections
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {gallerySections.map((section, index) => (
                                        <div
                                            key={section.id}
                                            className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                                        >
                                            <MdDragIndicator className="text-gray-400 cursor-move" />
                                            <Input
                                                placeholder="Section name (e.g., Front View, Side View)"
                                                value={section.name}
                                                onChange={(e) => updateGallerySection(index, e.target.value)}
                                                className="flex-1"
                                            />
                                            <Button
                                                danger
                                                icon={<BsTrash />}
                                                onClick={() => removeGallerySection(index)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </TabPane>
                </Tabs>

                {/* Footer Buttons */}
                <div className="flex justify-end gap-3 pt-6 mt-6 border-t">
                    <Button onClick={handleClose} size="large">
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleSubmit}
                        loading={submitLoading}
                        size="large"
                        className="bg-[#1955EB] hover:bg-[#1945E0]"
                    >
                        Update Subcategory
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

export default EditSubCategoryModal;
